import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessage, fieldErrors } from '../apollo/errors';
import { ErrorAlert } from '../components/QueryState';
import { OPENROUTER_MODELS_URL } from '../config';
import type { AdminOpenRouterModelsQuery, AdminTestModelMutation } from '../gql/graphql';
import {
  AdminOpenRouterModelsDocument,
  AdminSettingsDocument,
  AdminTestModelDocument,
} from '../graphql/operations';

/** What OpenRouter quotes prices in, and what this page shows them in. */
const TOKENS_PER_PRICE_UNIT = 1_000_000;

type Model = AdminOpenRouterModelsQuery['adminOpenRouterModels'][number];
type TestResult = AdminTestModelMutation['adminTestModel'];

/**
 * One prompt, one model, one answer — and what it cost.
 *
 * The question this screen exists for is the one the settings form cannot
 * answer: whether a model is reachable with the key this server has, what it
 * writes in Russian, and what a story's worth of tokens is actually billed at.
 * Nothing here is stored — a run leaves no row behind, and the model it was run
 * against is not the one stories are written with until it is saved on the
 * settings page.
 */
export function ModelTestPage() {
  const { t } = useTranslation();
  const catalogue = useQuery(AdminOpenRouterModelsDocument);
  // The model stories are actually written with, as the initial choice: the
  // operator is nearly always here to test that one, or to compare it.
  const settings = useQuery(AdminSettingsDocument);
  const [run, { data, loading: running, error: failure }] = useMutation(AdminTestModelDocument);

  const [chosen, setChosen] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');

  const models = catalogue.data?.adminOpenRouterModels;
  const model = chosen ?? settings.data?.adminSettings.openRouterModel ?? null;
  const fields = fieldErrors(failure);
  const result = data?.adminTestModel;

  async function generate() {
    // `run` rejects on an API error; the hook's own `error` is what the screen
    // renders, so there is nothing to do with the rejection but not to crash on
    // it.
    await run({ variables: { input: { model: model ?? '', prompt } } }).catch(() => undefined);
  }

  return (
    <Stack gap="md">
      <Title order={2}>{t('modelTest.title')}</Title>

      <Card withBorder padding="md">
        <Stack gap="md">
          {/* The catalogue comes from OpenRouter through the API. When it
              cannot be had, the id is still typeable — a broken dropdown is no
              reason not to be able to test a model. */}
          {catalogue.error && (
            <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
              {t('modelTest.noCatalogue', { reason: errorMessage(catalogue.error) ?? '' })}
            </Alert>
          )}

          {catalogue.error ? (
            <TextInput
              label={t('modelTest.model')}
              description={<ModelsLink />}
              placeholder={t('settings.modelPlaceholder')}
              error={fields.model}
              value={model ?? ''}
              onChange={(event) => {
                setChosen(event.currentTarget.value);
              }}
            />
          ) : (
            <ModelSelect
              models={models ?? []}
              value={model}
              error={fields.model}
              loading={catalogue.loading}
              onChange={setChosen}
            />
          )}

          <Textarea
            label={t('modelTest.prompt')}
            description={t('modelTest.promptDescription')}
            placeholder={t('modelTest.promptPlaceholder')}
            autosize
            minRows={8}
            error={fields.prompt}
            value={prompt}
            onChange={(event) => {
              setPrompt(event.currentTarget.value);
            }}
          />

          <Group justify="end">
            <Button
              loading={running}
              disabled={!model || prompt.trim().length === 0}
              onClick={() => void generate()}
            >
              {t('modelTest.generate')}
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Field errors are already under their inputs; this is the headline,
          which for this screen is usually upstream's own wording. */}
      {failure && Object.keys(fields).length === 0 && <ErrorAlert error={failure} />}

      {result && (
        <>
          <Card withBorder padding="md">
            <Stack gap="sm">
              <Text fw={600}>{t('modelTest.answer')}</Text>
              {/* Verbatim, newlines and all: what is being judged here is what
                  the model wrote, not a tidied version of it. */}
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {result.text}
              </Text>
            </Stack>
          </Card>

          <RequestInfo result={result} />
        </>
      )}
    </Stack>
  );
}

function ModelSelect({
  models,
  value,
  error,
  loading,
  onChange,
}: {
  models: readonly Model[];
  value: string | null;
  error: string | undefined;
  loading: boolean;
  onChange: (value: string | null) => void;
}) {
  const { t } = useTranslation();
  const { format } = useFormats();

  // The dropdown shows names and the form sends ids, so the price line needs a
  // way back from one to the other.
  const byId = useMemo(() => new Map(models.map((model) => [model.id, model])), [models]);

  return (
    <Select
      label={t('modelTest.model')}
      description={<ModelsLink />}
      placeholder={t('modelTest.modelPlaceholder')}
      searchable
      // A few hundred models, of which nobody scrolls past the first screen:
      // the search box is how one is found, not the list.
      limit={50}
      nothingFoundMessage={t('common.nothingFound')}
      disabled={loading}
      error={error}
      // A model that is stored but no longer listed would otherwise show as an
      // empty box, which reads as "nothing is set".
      data={dataFrom(models, value)}
      value={value}
      onChange={onChange}
      renderOption={({ option }) => {
        const model = byId.get(option.value);

        return (
          <Group justify="space-between" gap="sm" wrap="nowrap" w="100%">
            <Text size="sm">{option.label}</Text>
            {model && (
              <Text size="xs" c="dimmed">
                {format.pricing(model)}
              </Text>
            )}
          </Group>
        );
      }}
    />
  );
}

/** The stored model kept in the list even when the catalogue has dropped it. */
function dataFrom(
  models: readonly Model[],
  value: string | null,
): { value: string; label: string }[] {
  const data = models.map((model) => ({ value: model.id, label: model.name }));

  if (value && !models.some((model) => model.id === value)) data.unshift({ value, label: value });

  return data;
}

function ModelsLink() {
  const { t } = useTranslation();

  return (
    <>
      {t('modelTest.modelDescription')}{' '}
      <Anchor href={OPENROUTER_MODELS_URL} target="_blank" rel="noreferrer" inherit>
        {t('settings.modelsLink')}
      </Anchor>
    </>
  );
}

/**
 * What the run cost, which is the other half of what this page is for.
 *
 * A field upstream did not report is an em dash rather than a zero: "no number
 * came back" and "it was free" are different answers, and a relay that strips
 * the usage block gives the first one.
 */
function RequestInfo({ result }: { result: TestResult }) {
  const { t } = useTranslation();
  const { format } = useFormats();

  const rows: [string, string][] = [
    [t('modelTest.info.model'), result.model],
    [t('modelTest.info.promptTokens'), format.tokens(result.promptTokens)],
    [t('modelTest.info.completionTokens'), format.tokens(result.completionTokens)],
    [t('modelTest.info.totalTokens'), format.tokens(result.totalTokens)],
    [t('modelTest.info.cost'), format.cost(result.cost, result.costEstimated)],
    [t('modelTest.info.duration'), format.duration(result.durationMs)],
  ];

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Group gap="sm">
          <Text fw={600}>{t('modelTest.info.title')}</Text>
          {result.costEstimated && (
            <Badge color="gray" variant="light">
              {t('modelTest.info.estimated')}
            </Badge>
          )}
        </Group>

        <Table withRowBorders={false} verticalSpacing="xs">
          <Table.Tbody>
            {rows.map(([label, value]) => (
              <Table.Tr key={label}>
                <Table.Td w={220}>
                  <Text size="sm" c="dimmed">
                    {label}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{value}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

/**
 * The numbers on this page, in the operator's language: token counts grouped,
 * money to six decimals (a short prompt costs fractions of a cent, and two
 * decimals would round every one of them to zero).
 */
function useFormats() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  return useMemo(() => {
    const empty = t('common.empty');
    const tokens = new Intl.NumberFormat(language);
    const money = new Intl.NumberFormat(language, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 6,
    });
    const seconds = new Intl.NumberFormat(language, { maximumFractionDigits: 1 });

    return {
      format: {
        tokens: (value: number | null | undefined) =>
          typeof value === 'number' ? tokens.format(value) : empty,
        cost: (value: number | null | undefined, estimated: boolean) =>
          typeof value === 'number' ? `${estimated ? '≈ ' : ''}${money.format(value)}` : empty,
        duration: (ms: number) =>
          // Milliseconds below a second: "0 s" is what one decimal makes of a
          // fast answer, and how fast it was is half the point of measuring.
          ms < 1000
            ? t('modelTest.info.ms', { value: tokens.format(ms) })
            : t('modelTest.info.seconds', { value: seconds.format(ms / 1000) }),
        /** Both prices per million tokens, which is how the site quotes them. */
        pricing: (model: Model) => {
          if (model.promptPrice === null && model.completionPrice === null) {
            return t('modelTest.priceUnknown');
          }

          return t('modelTest.price', {
            input: money.format((model.promptPrice ?? 0) * TOKENS_PER_PRICE_UNIT),
            output: money.format((model.completionPrice ?? 0) * TOKENS_PER_PRICE_UNIT),
          });
        },
      },
    };
  }, [t, language]);
}
